import React, { useState, useEffect } from "react";
import { ControlsSwitch } from "../base/ControlsSwitch";
import { ControlsRow } from "../base/ControlsRow";
import { ImportantButton } from "../base/ImportantButton";
import { SecondaryButton } from "../base/SecondaryButton";
import { PrivateCategoriesSettingsControls } from "./../PrivateCategoriesSettingsControls";
import { BookCategoriesSettingsControls } from "./../BookCategoriesSettingsControls";
import { AddSubcategoryControls } from "./../AddSubcategoryControls";
import { AddCategoryControls } from "./../AddCategoryControls";
import { Spinner } from "../base/Spinner";
import { PanelDropdown } from "../base/PanelDropdown";
import { ControlsDropdown } from "../base/ControlsDropdown";
import {getSettings, updateSettings} from "api";
import "./SettingsControls.css";
import {useExtendedMutation, runAllMutationsWithArgs} from "mutations";

const LANGUAGE_OPTIONS = [
  { value: 'deen', label: 'German (DE→EN)' },
  { value: 'roen', label: 'Romanian (RO→EN)' },
  // { value: 'enen', label: 'English → English' },
  // { value: 'scoen', label: 'Scots → English' },
];

const SettingsControls = ({token, onClose, onApplied, variant = 'default'}) => {
  const Btn = variant === 'secondary' ? SecondaryButton : ImportantButton;
  const Dropdown = variant === 'secondary' ? PanelDropdown : ControlsDropdown;
  const [view, setView] = useState('main');
  const [privateCardsEnabled, setPrivateCardsEnabled] = useState(true);
  const [currentSettings, setCurrentSettings] = useState({});
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [hasPendingChange, setHasPendingChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isChildLoading, setIsChildLoading] = useState(false);
  const [selectedBookCategory, setSelectedBookCategory] = useState(null);
  const [selectedChapterSubcategory, setSelectedChapterSubcategory] = useState(null);

  const onChangeSwitch = () => {
    setPrivateCardsEnabled(!privateCardsEnabled);
    setHasPendingChange(true);
  }

  const onCurrentSettingsSuccess = (data) => {
    setCurrentSettings(data);
    if (data.privateCategory) setSelectedCategoryId(data.privateCategory);
    if (data.privateSubcategory) setSelectedSubcategoryId(data.privateSubcategory);
    if (data.language) setSelectedLanguage(data.language);
    if (data.bookCategory) setSelectedBookCategory(data.bookCategory);
    if (data.chapterSubcategory) setSelectedChapterSubcategory(data.chapterSubcategory);
    if (data.privateCategoriesEnabled === false) setPrivateCardsEnabled(false);
  }

  const onError = (error) => {
    console.log(error);
  }

  const currentSettingsMutation = useExtendedMutation(getSettings, onCurrentSettingsSuccess, onError);
  const updateSettingsMutation = useExtendedMutation(updateSettings, () => { onClose(); if (onApplied) onApplied(); }, onError);

  useEffect(() => {
    runAllMutationsWithArgs([
        {mutation: currentSettingsMutation, input: {token: token}},
      ])
  }, []); //TODO rewrite this so that you first fetch the current settings, probably somewhere else and then here it will depend on it.

  const handleSelectionChange = ({ categoryId, subcategoryId }) => {
    if (categoryId !== null) setSelectedCategoryId(categoryId);
    if (subcategoryId !== null) setSelectedSubcategoryId(subcategoryId);
    setHasPendingChange(true);
  };

  const handleBookSelectionChange = ({ bookCategory, chapterSubcategory }) => {
    if (bookCategory !== null) setSelectedBookCategory(bookCategory);
    if (chapterSubcategory !== null) setSelectedChapterSubcategory(chapterSubcategory);
    setHasPendingChange(true);
  };

  const handleCancel = () => {
    if (variant === 'secondary') {
      setSelectedCategoryId(currentSettings.privateCategory ?? null);
      setSelectedSubcategoryId(currentSettings.privateSubcategory ?? null);
      setSelectedLanguage(currentSettings.language ?? null);
      setSelectedBookCategory(currentSettings.bookCategory ?? null);
      setSelectedChapterSubcategory(currentSettings.chapterSubcategory ?? null);
      setHasPendingChange(false);
    }
    onClose();
  };

  const toInt = (value) => typeof value === 'number' ? Math.trunc(value) : value;

  const handleSave = () => {
    setHasPendingChange(false);
    updateSettingsMutation.mutateOnVirtualThread({
      token,
      privateCategory: selectedCategoryId,
      privateSubcategory: selectedSubcategoryId,
      language: selectedLanguage,
      privateCategoriesEnabled: String(privateCardsEnabled),
      bookCategory: selectedBookCategory,
      chapterSubcategory: toInt(selectedChapterSubcategory),
    });
  };

  const isAnyLoading = isLoading || isChildLoading || updateSettingsMutation.isPending;

  return (
    <>
      {isAnyLoading && <Spinner />}
      <div style={{ display: isAnyLoading ? 'none' : 'contents' }}>
        {view === 'editCategoriesMenu' && (
          <>
            <ControlsRow>
              <Btn buttonText={"Add Category"} onClick={() => setView('addCategory')} />
            </ControlsRow>
            <ControlsRow>
              <Btn buttonText={"Add Subcategory"} onClick={() => setView('addSubcategory')} />
            </ControlsRow>
            <ControlsRow>
              <Btn buttonText={"Back"} onClick={() => setView('main')} />
            </ControlsRow>
          </>
        )}
        {view === 'addCategory' && (
          <AddCategoryControls
            token={token}
            onBack={() => setView('editCategoriesMenu')}
            onSaved={() => setView('main')}
            onLoadingChange={setIsChildLoading}
            variant={variant}
          />
        )}
        {view === 'addSubcategory' && (
          <AddSubcategoryControls
            token={token}
            onBack={() => setView('editCategoriesMenu')}
            onSaved={() => setView('main')}
            categoryId={selectedCategoryId}
            onLoadingChange={setIsChildLoading}
            variant={variant}
          />
        )}
        {view === 'main' && (
          <>
            <ControlsRow>
              <Dropdown
                label="Language"
                options={LANGUAGE_OPTIONS}
                value={selectedLanguage ?? ''}
                onChange={(e) => {
                setSelectedLanguage(e.target.value);
                setSelectedBookCategory(null);
                setSelectedChapterSubcategory(null);
                setHasPendingChange(true);
              }}
              />
            </ControlsRow>
            <ControlsRow>
              {privateCardsEnabled ? (
                <PrivateCategoriesSettingsControls
                  token={token}
                  onSelectionChange={handleSelectionChange}
                  currentCategoryId={selectedCategoryId}
                  currentSubcategoryId={selectedSubcategoryId}
                  onLoaded={() => setIsLoading(false)}
                  onLoadingChange={setIsChildLoading}
                  variant={variant}
                />
              ) : (
                <BookCategoriesSettingsControls
                  token={token}
                  language={selectedLanguage}
                  onSelectionChange={handleBookSelectionChange}
                  currentBookCategory={selectedBookCategory}
                  currentChapterSubcategory={selectedChapterSubcategory}
                  onLoaded={() => setIsLoading(false)}
                  onLoadingChange={setIsChildLoading}
                  variant={variant}
                />
              )}
              <ControlsSwitch
                labelOn={"Private Cards"}
                labelOff={"Book Mode"}
                onChange={onChangeSwitch}
                toggle={privateCardsEnabled}
                disabled={selectedLanguage !== 'deen'}
                disabledTooltip={"Book mode is not yet available for this language"}
              />
            </ControlsRow>
            <ControlsRow>
              <div className="sc-mobile-only">
                <Btn buttonText={"Edit Categories"} onClick={() => setView('editCategoriesMenu')} />
              </div>
            </ControlsRow>
            <ControlsRow horizontalOnMobile={true}>
              <Btn buttonText={"Cancel"} onClick={handleCancel} />
              <div className="sc-desktop-only">
                <Btn buttonText={"Edit Categories"} onClick={() => setView('editCategoriesMenu')} />
              </div>
              <div className={hasPendingChange ? 'sc-apply-pending' : ''}>
                <Btn buttonText={"Apply"} onClick={handleSave} />
              </div>
            </ControlsRow>
          </>
        )}
      </div>
    </>
  );
}

export { SettingsControls }
